import { Box, createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import * as React from 'react';
import moment from 'moment';
import NewPaymentTermsRowsConnected from '../../../../containers/NegotiationDebts/ConfirmationView/NewPaymentTermsRowsConnected';
import { NegotiationOption, NegotiationOptionAlternative, OutDebt } from '../../../../generated/graphql';
import { getNegBooleans } from '../../../../utils/getNegBooleans';
import thousandSeparator from '../../../../utils/thousandSeparator';
import Alert from '../../../Alert/Alert';
import RenderText from '../../../../dictionaries/RenderText';
import { useSpace } from '../../../../content/utils/useSpace';
import MarkdownRender from '../../../../dictionaries/MarkdownRender';

interface NewPaymentTermsContentProps {
  negOption: NegotiationOption;
  alternative: NegotiationOptionAlternative;
  outDebt: OutDebt;
  // eslint-disable-next-line react/no-unused-prop-types
  inSummary?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  parentDebt?: OutDebt;
}

const NewPaymentTermsContent: React.FC<NewPaymentTermsContentProps> = (props) => {
  const { negOption, alternative, outDebt } = props;
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const {
    isRefinance, isACPK, isPaymentPromise, isTotalPayment,
    isBbva, isPichincha, isBogotaLowRate, isCatchUp,
    isExpressRefinance, isBogota, isBalanceBehindPayment, isBogotaTraditional,
    isBogotaNoPayment, isBogotaSpecialCampaign, isScotiabank, isMovistarMoney, isJcap, isPra,
  } = getNegBooleans(outDebt, negOption, alternative);
  let termTitle = `Nueva cuota${isRefinance || isACPK ? ' mensual' : ''} aprox`;
  if (isPaymentPromise) termTitle = 'Valor a pagar aproximado';
  if (isPaymentPromise && isBogota) termTitle = 'Valor a pagar aproximado (Honorarios incluídos)';
  if (isPaymentPromise && isScotiabank) termTitle = 'Valor a pagar';
  if (isRefinance && isScotiabank) termTitle = 'Nueva cuota mensual aprox. (no incluye seguros y otros cargos)';
  if (isTotalPayment) termTitle = 'Valor final a pagar';
  if (isCatchUp && isMovistarMoney) termTitle = 'Valor a pagar';

  const showCollectionFeesAlert = isPaymentPromise && isBbva;
  const showBoxResume = !(isBogota && isBogotaNoPayment && isBalanceBehindPayment);
  const showTermDescription = !((isJcap || isPra) && isTotalPayment);

  let termAmountContent = <>{`$ ${thousandSeparator(alternative?.value || 0)}`}</>;

  if (isBogota && isBalanceBehindPayment) {
    if (isBogotaTraditional) termTitle = 'Valor a pagar';
    if (isBogotaSpecialCampaign) termTitle = 'Valor a pagar';
    if (isBogotaLowRate && outDebt?.extra?.popupRate) {
      termTitle = 'Nueva Tasa Efectiva Anual al';
      termAmountContent = <>{`${outDebt.extra.popupRate}%`}</>;
    }
    if (isBogotaNoPayment) termTitle = 'Valor de Abono Sugerido';
  }

  if (isPichincha && isExpressRefinance) termTitle = 'Nueva cuota aprox';
  const maxPaymentDate = negOption.settings?.maxPaymentDate;

  const stateAlertSpace = useSpace('NEG_OPTION_CONDITIONS', 'alert', { outDebt, option: negOption, alternative });

  if (!alternative) return null;

  return (
    <>
      <NewPaymentTermsRowsConnected
        className="mb-3"
        outDebt={outDebt}
        negOption={negOption}
        alternative={alternative}
      />
      {showBoxResume
      && (
      <Box className={classes.boxResume} data-testid="new-payment-terms-content__box-resume">
        <Typography className={classes.newTermTitle}>{termTitle}</Typography>
        <Typography className={classes.newTermAmount} data-testid="new-term-amount__box-resume">{termAmountContent}</Typography>
        {showTermDescription && (
          <Typography className={classes.newTermDescription}>
            <RenderText
              isMarkdown
              textKey={`negotiation_${negOption.type}_newTermDescription`}
              context={{
                negPaymentDate: moment(maxPaymentDate).format('DD MMM YYYY'),
              }}
            />
          </Typography>
        )}
      </Box>
      )}
      {showCollectionFeesAlert
      && (
      <Alert color="warning" icon="info" iconColor="warning" filled className={classes.collectionFeesAlert}>
        <RenderText isMarkdown textKey="debt_newPaymentTerms_PaymentPromise_collectionFees_alert" />
      </Alert>
      )}
      {stateAlertSpace.visible && (
        <Box className={classes.detailsBox}>
          <Alert
            color="gray"
            title="Ten en cuenta"
            filled={false}
            icon="info"
            iconColor="primary"
            className={classes.newTermAlert}
          >
            <MarkdownRender text={stateAlertSpace.text!} />
          </Alert>
        </Box>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => createStyles({
  detailsBox: {
    display: 'grid',
    marginBlockStart: theme.spacing(2),
  },
  collectionFeesAlert: {
    color: theme.palette.indigo.main,
    margin: theme.spacing(2, 0),
  },
  newTermAlert: {
    color: theme.palette.indigo.main,
  },
  boxResume: {
    padding: theme.spacing(1.5, 2),
    boxSizing: 'border-box',
    borderRadius: theme.spacing(1),
    backgroundColor: `${theme.palette.lavender.main}15`,
    position: 'relative',
    textAlign: 'center',
  },
  newTermDescription: {
    color: theme.palette.indigo.main,
    fontSize: '0.9rem',
  },
  newTermDiscount: {
    position: 'absolute',
    insetBlockStart: '-20%',
    insetInlineStart: `${theme.spacing(1)}px`,
    backgroundColor: theme.palette.lavender.main,
    padding: theme.spacing(0.5, 0.75),
    borderRadius: theme.spacing(0.5),
    ...theme.typography.xsmall as any,
    color: theme.palette.pearl.main,

    display: 'flex',
    alignItems: 'center',
    columnGap: theme.spacing(0.5),
    '& span': {
      fontWeight: 300,
    },
  },
  newTermTitle: {
    ...theme.typography.capitalize as any,
    textTransform: 'uppercase',
    color: theme.palette.lavender.main,
  },
  newTermAmount: {
    ...theme.typography.h4,
    color: theme.palette.lavender.main,
    fontWeight: 600,
    '& strong': {
      ...theme.typography.body1,
      color: theme.palette.lavender.main,
      marginLeft: theme.spacing(1),
      fontWeight: 300,
    },
  },
}));

export default NewPaymentTermsContent;
